import React, { useMemo } from 'react'

import { useLayerApiUrl } from './ImportDataContext'
import {
  type CustomAccount,
  getTimeTrackingImportData,
  ImportApiError,
  type TimeTrackingEntry,
  type TimeTrackingImportData,
  type TimeTrackingService,
  type TimeTrackingServiceResponse,
  validateTimeTrackingImportData,
} from './importDummyDataHelpers'
import { ImportForm } from './ImportForm'
import { type ImportStepDef } from './useImportForm'

type CreateTimeEntryPayload = Omit<TimeTrackingEntry, 'service_external_id'> & {
  service_id: string
}

export interface ImportTimeTrackingProps {
  demoName: string
  businessId: string
  businessAccessToken: string
  defaultCustomAccount: CustomAccount
  activationAt: string
}

const extractService = (serviceData: unknown): TimeTrackingServiceResponse => {
  const service =
    typeof serviceData === 'object' && serviceData !== null && 'data' in serviceData
      ? (serviceData as { data: unknown }).data
      : serviceData

  if (
    typeof service !== 'object'
    || service === null
    || typeof (service as TimeTrackingServiceResponse).id !== 'string'
    || typeof (service as TimeTrackingServiceResponse).external_id !== 'string'
  ) {
    throw new Error(
      `Unexpected service response shape: ${JSON.stringify(serviceData)}`,
    )
  }

  return service as TimeTrackingServiceResponse
}

const createService = async (
  businessId: string,
  businessAccessToken: string,
  service: TimeTrackingService,
  layerApiUrl: string,
) => {
  const response = await fetch(
    `${layerApiUrl}/v1/businesses/${businessId}/catalog/services`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${businessAccessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(service),
    },
  )

  if (!response.ok) {
    const errorText = await response.text()
    throw new ImportApiError(
      `Service creation failed (${response.status}): ${errorText}`,
      response.status,
    )
  }

  return extractService(await response.json())
}

const createTimeEntries = async (
  businessId: string,
  businessAccessToken: string,
  timeEntries: CreateTimeEntryPayload[],
  layerApiUrl: string,
) => {
  const response = await fetch(
    `${layerApiUrl}/v1/businesses/${businessId}/time-tracking/time-entries/bulk`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${businessAccessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ time_entries: timeEntries }),
    },
  )

  if (!response.ok) {
    const errorText = await response.text()
    throw new ImportApiError(
      `Time entry creation failed (${response.status}): ${errorText}`,
      response.status,
    )
  }
}

const buildTimeTrackingSteps = (
  businessId: string,
  businessAccessToken: string,
  layerApiUrl: string,
) => (data: TimeTrackingImportData): ImportStepDef[] => {
  const batchSize = 100
  const batchCount = Math.ceil(data.time_entries.length / batchSize)
  const serviceIdByExternalId = new Map<string, string>()

  return [
    ...data.services.map((service, index) => ({
      label: `Creating time tracking service ${index + 1}/${data.services.length}`,
      run: async () => {
        const createdService = await createService(
          businessId,
          businessAccessToken,
          service,
          layerApiUrl,
        )
        serviceIdByExternalId.set(
          createdService.external_id,
          createdService.id,
        )
      },
    })),
    ...Array.from({ length: batchCount }, (_, batchIndex) => ({
      label: `Creating time entries batch ${batchIndex + 1}/${batchCount}`,
      run: async () => {
        const start = batchIndex * batchSize
        const batch = data.time_entries
          .slice(start, start + batchSize)
          .map(entry => {
            const serviceId = serviceIdByExternalId.get(entry.service_external_id)
            if (!serviceId) {
              throw new Error(
                `Missing service id for ${entry.service_external_id}`,
              )
            }

            const { service_external_id: _serviceExternalId, ...entryPayload } =
              entry
            return {
              ...entryPayload,
              service_id: serviceId,
            }
          })

        await createTimeEntries(businessId, businessAccessToken, batch, layerApiUrl)
      },
    })),
  ]
}

export const ImportTimeTracking = ({
  demoName,
  businessId,
  businessAccessToken,
  defaultCustomAccount,
  activationAt,
}: ImportTimeTrackingProps) => {
  const layerApiUrl = useLayerApiUrl()
  const getSteps = useMemo(
    () => buildTimeTrackingSteps(businessId, businessAccessToken, layerApiUrl),
    [businessId, businessAccessToken, layerApiUrl],
  )

  return (
    <ImportForm
      title='Time Tracking'
      defaultCustomAccount={defaultCustomAccount}
      getInitialData={() => getTimeTrackingImportData(demoName, activationAt)}
      validateData={validateTimeTrackingImportData}
      getSteps={getSteps}
      dependencies={[demoName, activationAt]}
    />
  )
}
